/**
 * CSV helpers for bulk account import on Add Account page.
 */

const HEADER_ALIASES = {
  name: "Name",
  accountname: "Name",
  mobile_no: "Mobile_No",
  mobile: "Mobile_No",
  mobileno: "Mobile_No",
  phone_no: "Phone_No",
  phone: "Phone_No",
  phoneno: "Phone_No",
  email_address: "Email_Address",
  email: "Email_Address",
  description: "Description",
  account_type: "Account_Type",
  accounttype: "Account_Type",
  account_type_id: "Account_Type_ID",
  accounttypeid: "Account_Type_ID",
  openingbalance: "OpeningBalance",
  opening_balance: "OpeningBalance",
  openingbalancetype: "OpeningBalanceType",
  opening_balance_type: "OpeningBalanceType",
  balancetype: "OpeningBalanceType",
  balance_type: "OpeningBalanceType",
  openingdate: "OpeningDate",
  opening_date: "OpeningDate",
};

function normalizeHeader(h) {
  return (h || "").trim().toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
}

function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

export function buildSampleImportRows() {
  const today = new Date().toISOString().slice(0, 10);
  return [
    {
      Name: "Ali Traders",
      Mobile_No: "03001234567",
      Phone_No: "",
      Email_Address: "ali@example.com",
      Description: "Sample customer account",
      Account_Type: "Customer",
      OpeningBalance: 5000,
      OpeningBalanceType: "Debit",
      OpeningDate: today,
    },
    {
      Name: "ABC Petroleum Supplier",
      Mobile_No: "03007654321",
      Phone_No: "0421234567",
      Email_Address: "",
      Description: "Sample supplier account",
      Account_Type: "Supplier",
      OpeningBalance: 3000,
      OpeningBalanceType: "Credit",
      OpeningDate: today,
    },
    {
      Name: "HBL Bank Account",
      Mobile_No: "",
      Phone_No: "",
      Email_Address: "",
      Description: "Sample bank account",
      Account_Type: "Bank",
      OpeningBalance: 25000,
      OpeningBalanceType: "Debit",
      OpeningDate: today,
    },
    {
      Name: "Office Rent Expense",
      Mobile_No: "",
      Phone_No: "",
      Email_Address: "",
      Description: "Sample expense account",
      Account_Type: "Expense",
      OpeningBalance: 0,
      OpeningBalanceType: "Debit",
      OpeningDate: today,
    },
    {
      Name: "Employee - Ahmed",
      Mobile_No: "03001112233",
      Phone_No: "",
      Email_Address: "",
      Description: "Sample employee advance",
      Account_Type: "Employee",
      OpeningBalance: 1000,
      OpeningBalanceType: "Debit",
      OpeningDate: today,
    },
  ];
}

export function downloadSampleCsv(filename = "account_import_sample.csv") {
  const rows = buildSampleImportRows();
  const headers = [
    "Name",
    "Mobile_No",
    "Phone_No",
    "Email_Address",
    "Description",
    "Account_Type",
    "OpeningBalance",
    "OpeningBalanceType",
    "OpeningDate",
  ];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ];
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function mapRow(raw, accountTypeOptions) {
  const mapped = {};
  Object.keys(raw).forEach((key) => {
    const norm = normalizeHeader(key);
    const field = HEADER_ALIASES[norm] || key;
    mapped[field] = (raw[key] ?? "").toString().trim();
  });

  if (!mapped.Name) return null;

  let accountTypeId = parseInt(mapped.Account_Type_ID, 10);
  if (!accountTypeId && mapped.Account_Type && accountTypeOptions?.length) {
    const match = accountTypeOptions.find(
      (t) =>
        t.label.toLowerCase() === mapped.Account_Type.toLowerCase() ||
        String(t.value) === mapped.Account_Type
    );
    if (match) accountTypeId = match.value;
  }

  let openingBalance = parseFloat(mapped.OpeningBalance);
  if (Number.isNaN(openingBalance)) openingBalance = 0;

  let balanceType = mapped.OpeningBalanceType || "Debit";
  if (balanceType.toLowerCase() === "receivable") balanceType = "Debit";
  if (balanceType.toLowerCase() === "payable") balanceType = "Credit";

  return {
    Name: mapped.Name,
    Mobile_No: mapped.Mobile_No || "",
    Phone_No: mapped.Phone_No || "",
    Email_Address: mapped.Email_Address || "",
    Description: mapped.Description || "",
    Account_Type_ID: accountTypeId || 0,
    Account_Type: mapped.Account_Type || "",
    OpeningBalance: openingBalance,
    OpeningBalanceType: balanceType,
    OpeningDate: mapped.OpeningDate || new Date().toISOString().slice(0, 10),
    Account_Type_BE: mapped.Account_Type ? { Name: mapped.Account_Type } : null,
  };
}

export function parseAccountsCsv(text, accountTypeOptions) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]);
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    if (cells.every((c) => !c.trim())) continue;
    const raw = {};
    headers.forEach((h, idx) => {
      raw[h] = cells[idx] ?? "";
    });
    const row = mapRow(raw, accountTypeOptions);
    if (row) rows.push(row);
  }
  return rows;
}
